// react
import { useEffect, useRef } from 'react';
import type { Step } from 'react-joyride';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { createFileRoute, useSearch } from '@tanstack/react-router';

// api
import { useSensorsGetCollectionAsyncByqueryOptionsQuery } from '@/api/generated/recalc/endpoints/v1/sensors';

// shared components
import { getSensorOverviewTour } from '@/components/joyride/guided-tours/SensorOverview.tour';
import useAppTour from '@/components/joyride/useAppTour';
import PageContainer from '@/components/layout/PageContainer';
import PageHeader from '@/components/layout/PageHeader';
import LinearLoadingIndicator from '@/components/ui/progress/LinearLoadingIndicator';

// feature-components
import SensorDataGrid from '@/features/sensors/components/SensorDataGrid';

export const Route = createFileRoute('/$accountId/_user/sensors/')({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation('units-gateways-sensors');
  const search = useSearch({ strict: false });
  const hasAutoStartedTourRef = useRef(false);
  const { accountId } = Route.useParams();
  const accountIdAsNumber = Number(accountId);
  const isAccountIdValid = Number.isFinite(accountIdAsNumber);

  const { controls, Tour } = useAppTour({
    steps: getSensorOverviewTour() as Step[],
  });

  useEffect(() => {
    const isSensorOverviewTour =
      search?.tour === 'sensors-overview' || search?.tour === 'sensors';

    if (isSensorOverviewTour && !hasAutoStartedTourRef.current) {
      hasAutoStartedTourRef.current = true;
      controls.start();
    } else if (!search?.tour) {
      controls.close();
    }
  }, [controls, search?.tour]);

  const {
    data: sensors,
    isLoading,
    isFetching,
  } = useSensorsGetCollectionAsyncByqueryOptionsQuery(
    {
      orderby: 'SensorId',
      'Account.AccountId': `eq(${accountIdAsNumber})`,
      top: 10000,
    },
    { skip: !isAccountIdValid },
  );

  return (
    <>
      <LinearLoadingIndicator isLoading={isLoading || isFetching} />
      <PageContainer
        maxWidth="xl"
        sx={{ py: 2 }}
        data-testid="sensor-overview-container"
      >
        {Tour}
        <PageHeader title={t('sensors.overviewTitle')} disableArrow={true} />
        <SensorDataGrid
          accountId={accountIdAsNumber}
          sensors={sensors}
          isLoading={isLoading}
          data-testid="sensor-overview-datagrid"
        />
      </PageContainer>
    </>
  );
}
